import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Input from '../input';
import {
  otherGroupsFormEstablishmentTypes,
  schoolFormEstablishmentTypes,
  typeQuries,
} from './data';
import { getWSFRegionalInformation } from '../../service';
import { isEmpty } from 'lodash';

/**
 * @param {
 *  type: 'school' | 'other-group',
 *  translationPrefix: string
 * } props
 * @returns
 */
const EstablishmentDetailsForm = ({ type = 'school', formik, translationPrefix }) => {
  const { t } = useTranslation();
  const establishmentDetailsFormikValues = formik.values?.establishmentDetails || {};

  const [postalCodeOptions, setPostalCodeOptions] = useState([]);
  const [postalOptionsLoading, setPostalOptionsLoading] = useState(false);
  const [nameOptionsLoading, setNameOptionsLoading] = useState(false);
  const [establishmentNameOptions, setEstablishmentNameOptions] = useState([]);
  const [manualAddress, setManualAddress] = useState(false);

  const fetchPostalCodeOptions = async ({ searchType, searchValue }) => {
    try {
      setPostalOptionsLoading(true);
      let where = typeQuries[searchType];
      if (searchValue) {
        where = `startswith(code_postal,'${searchValue}') AND ${where}`;
      }

      const { results } = await getWSFRegionalInformation({ where });

      const tempList = [];
      results.forEach((result) => {
        if (!tempList.find((item) => item.value === result.code_postal)) {
          tempList.push({
            value: result.code_postal,
            label: result.code_postal,
          });
        }
      });

      setPostalCodeOptions(tempList);
    } catch (err) {
      console.log('error in postalcode options search', err);
      const message = err?.response?.data?.message || 'Une erreur est survenue';
      toast.error(message);
    } finally {
      setPostalOptionsLoading(false);
    }
  };

  const fetchEstablishmentNameOptions = async ({ searchType }) => {
    try {
      setNameOptionsLoading(true);
      const where = typeQuries[searchType];
      const { results } = await getWSFRegionalInformation({
        where: `code_postal="${establishmentDetailsFormikValues.postalCode}" AND ${where}`,
      });

      const mappedResults = results.map((result) => ({
        value: result.nom_etablissement,
        label: result.nom_etablissement,
        ...result,
      }));

      mappedResults.sort((a, b) => a.label.localeCompare(b.label));

      setEstablishmentNameOptions(mappedResults);
    } catch (err) {
      console.log('error in establishment name options search', err);
      const message = err?.response?.data?.message || 'Une erreur est survenue';
      toast.error(message);
    } finally {
      setNameOptionsLoading(false);
    }
  };

  useEffect(() => {
    if (type !== 'school') return;
    if (isEmpty(establishmentDetailsFormikValues.postalCode)) {
      formik.setFieldValue('establishmentDetails.name', '');
      formik.setFieldValue('establishmentDetails.city', '');
      formik.setFieldValue('establishmentDetails.address', '');
      formik.setFieldValue('establishmentDetails.department', '');
      setEstablishmentNameOptions([]);
      return;
    }

    fetchEstablishmentNameOptions({ searchType: establishmentDetailsFormikValues.type });
  }, [establishmentDetailsFormikValues.postalCode]);

  useEffect(() => {
    if (
      type !== 'school' ||
      isEmpty(establishmentDetailsFormikValues.name) ||
      !establishmentNameOptions?.length
    )
      return;
    if (
      isEmpty(establishmentDetailsFormikValues.postalCode) ||
      isEmpty(establishmentDetailsFormikValues.type) ||
      isEmpty(establishmentDetailsFormikValues.name)
    )
      return;

    const selectedObject = establishmentNameOptions.find(
      (option) => option.value === establishmentDetailsFormikValues.name,
    );

    formik.setFieldValue('establishmentDetails.city', selectedObject?.nom_commune || '');
    formik.setFieldValue('establishmentDetails.address', selectedObject?.adresse_1 || '');
    formik.setFieldValue(
      'establishmentDetails.department',
      selectedObject?.libelle_departement || '',
    );

    formik.setFieldError('establishmentDetails.city', '');
    formik.setFieldError('establishmentDetails.address', '');
    formik.setFieldError('establishmentDetails.department', '');

    setManualAddress(isEmpty(selectedObject?.adresse_1));
  }, [establishmentDetailsFormikValues.name, establishmentNameOptions]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6">
      <div className="col-span-1">
        <Input
          name={'type'}
          id={'type'}
          label={t(`${translationPrefix}.type.label`)}
          placeholder={t(`${translationPrefix}.type.placeholder`)}
          type={'select'}
          options={
            type === 'school'
              ? [...schoolFormEstablishmentTypes]
              : [...otherGroupsFormEstablishmentTypes]
          }
          defaultValue={establishmentDetailsFormikValues.type}
          onChange={(e) => {
            formik.setFieldValue('establishmentDetails.type', e.target.value);
            if (type !== 'school') return;

            formik.setFieldValue('establishmentDetails.postalCode', '');
            formik.setFieldValue('establishmentDetails.name', '');
            formik.setFieldValue('establishmentDetails.city', '');
            formik.setFieldValue('establishmentDetails.address', '');
            formik.setFieldValue('establishmentDetails.department', '');
            setPostalCodeOptions([]);
            setEstablishmentNameOptions([]);

            fetchPostalCodeOptions({ searchType: e.target.value });
          }}
          error={
            formik.touched?.establishmentDetails?.type && formik.errors?.establishmentDetails?.type
          }
        />
      </div>

      <div className="col-span-1">
        <Input
          name={'postalCode'}
          id={'postalCode'}
          label={t(`${translationPrefix}.postalCode.label`)}
          placeholder={t(`${translationPrefix}.postalCode.placeholder`)}
          type={type === 'school' ? 'select' : 'text'}
          options={postalCodeOptions}
          defaultValue={establishmentDetailsFormikValues.postalCode}
          setSearch={
            type === 'school'
              ? ({ query, result }) => {
                  if (result) {
                    formik.setFieldValue('establishmentDetails.postalCode', result);
                    setPostalCodeOptions([]);
                    return;
                  }

                  fetchPostalCodeOptions({
                    searchType: establishmentDetailsFormikValues.type,
                    searchValue: query,
                  });
                }
              : undefined
          }
          onChange={(e) => {
            formik.setFieldValue('establishmentDetails.postalCode', e.target.value);
          }}
          error={
            formik.touched?.establishmentDetails?.postalCode &&
            formik.errors?.establishmentDetails?.postalCode
          }
          loading={postalOptionsLoading}
        />
      </div>

      <div className="col-span-1">
        <Input
          name={'name'}
          id={'name'}
          label={t(`${translationPrefix}.name.label`)}
          placeholder={t(`${translationPrefix}.name.placeholder`)}
          type={type === 'school' ? 'select' : 'text'}
          options={establishmentNameOptions}
          onChange={(e) => {
            formik.setFieldValue('establishmentDetails.name', e.target.value);
          }}
          error={
            formik.touched?.establishmentDetails?.name && formik.errors?.establishmentDetails?.name
          }
          defaultValue={establishmentDetailsFormikValues.name}
          loading={nameOptionsLoading}
        />

        {type === 'school' && (
          // || establishmentDetailsFormikValues.type === 'CFA' || establishmentDetailsFormikValues.type === 'MFR')
          <div
            className="bg-gray-40 rounded-xl px-3 py-3 text-sm text-black mt-2"
            dangerouslySetInnerHTML={{
              __html: t(`${translationPrefix}.name.description`),
            }}
          />
        )}
      </div>

      <div className="col-span-1">
        <Input
          name={'city'}
          id={'city'}
          label={t(`${translationPrefix}.city.label`)}
          placeholder={t(`${translationPrefix}.city.placeholder`)}
          type={'text'}
          defaultValue={establishmentDetailsFormikValues.city}
          onChange={(e) => {
            if (type === 'school') return;

            formik.setFieldValue('establishmentDetails.city', e.target.value);
          }}
          error={
            formik.touched?.establishmentDetails?.city && formik.errors?.establishmentDetails?.city
          }
        />
      </div>

      <div className="col-span-1">
        <Input
          name={'address'}
          id={'address'}
          label={t(`${translationPrefix}.address.label`)}
          placeholder={t(`${translationPrefix}.address.placeholder`)}
          type={'text'}
          defaultValue={establishmentDetailsFormikValues.address}
          onChange={(e) => {
            if (!manualAddress && type === 'school') return;

            formik.setFieldValue('establishmentDetails.address', e.target.value);
          }}
          error={
            formik.touched?.establishmentDetails?.address &&
            formik.errors?.establishmentDetails?.address
          }
        />
      </div>

      <div className="col-span-1">
        <Input
          name={'department'}
          id={'department'}
          label={t(`${translationPrefix}.department.label`)}
          placeholder={t(`${translationPrefix}.department.placeholder`)}
          type={'text'}
          defaultValue={establishmentDetailsFormikValues.department}
          onChange={(e) => {
            if (type === 'school') return;

            formik.setFieldValue('establishmentDetails.department', e.target.value);
          }}
          error={
            formik.touched?.establishmentDetails?.department &&
            formik.errors?.establishmentDetails?.department
          }
        />
      </div>

      {type === 'school' && (
        <div className="col-span-1">
          <Input
            name={'privateContract'}
            id={'privateContract'}
            paragraphLabel={t(`${translationPrefix}.privateContract.label`)}
            placeholder={t(`${translationPrefix}.privateContract.placeholder`)}
            type={'radio'}
            options={[
              {
                value: 'false',
                label: t(`${translationPrefix}.privateContract.false`),
              },
              {
                value: 'true',
                label: t(`${translationPrefix}.privateContract.true`),
              },
            ]}
            defaultValue={establishmentDetailsFormikValues.privateContract || 'false'}
            onChange={(value) => {
              formik.setFieldValue('establishmentDetails.privateContract', value);
            }}
            error={formik.errors?.establishmentDetails?.privateContract}
          />
        </div>
      )}

      {type === 'school' && (
        <div className="col-span-1">
          <Input
            name={'rep'}
            id={'rep'}
            paragraphLabel={t(`${translationPrefix}.rep.label`)}
            placeholder={t(`${translationPrefix}.rep.placeholder`)}
            type={'radio'}
            defaultValue={establishmentDetailsFormikValues.rep || 'false'}
            options={[
              {
                value: 'false',
                label: t(`${translationPrefix}.rep.false`),
              },
              {
                value: 'true',
                label: t(`${translationPrefix}.rep.true`),
              },
            ]}
            onChange={(value) => {
              formik.setFieldValue('establishmentDetails.rep', value);
            }}
            error={formik.errors?.establishmentDetails?.rep}
          />
        </div>
      )}
    </div>
  );
};

export default EstablishmentDetailsForm;
